import React, { useMemo, useState, useEffect } from "react";
import { Hooks, Util, Context } from "@xt/xtbase";
const { useTranslation } = Hooks;
const { XtContext } = Context;
const { getUrl, moment } = Util;
import { Modal } from "antd";
import { getAssetTrend } from "@/api/xt-report";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { observer } from "mobx-react-lite";
import styles from "./index.module.less";

// 展开/收起图标
const ExpandIcon = ({ expanded, onClick }) => (
  <svg
    width="10"
    height="7"
    viewBox="0 0 10 7"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    onClick={onClick}
    className={`${styles["expand-icon"]} ${expanded ? styles["expand-icon--expanded"] : ""}`}
  >
    <path
      d="M5 6.24001L5.72394e-08 1.44001L1.25 0.240006L5 3.84001L8.75 0.240006L10 1.44001L5 6.24001Z"
      fill="white"
    />
  </svg>
);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler
);

const AssetChart = ({ isLogin, chartData: propChartData, expanded, onToggleExpand }) => {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const { currency } = appState;
  const [selectedPeriod, setSelectedPeriod] = useState("7");
  const [chartData, setChartData] = useState(propChartData || null);
  const [loading, setLoading] = useState(false);

  const handleDeposit = () => {
    const url = getUrl("/wallet/deposit");
    window.location.href = url;
  };

  const handleBuyCrypto = () => {
    const url = getUrl("/wallet/buy-crypto");
    window.location.href = url;
  };

  // 获取资产趋势数据（切换天数时重新调用接口）
  const fetchAssetTrend = async (days, converts) => {
    if (!isLogin) return;
    try {
      setLoading(true);
      const res = await getAssetTrend({
        params: {
          days: parseInt(days),
          converts: converts.toUpperCase(),
        },
      });
      if (res && Array.isArray(res)) {
        setChartData(res);
      } else {
        setChartData(null);
      }
    } catch (error) {
      console.error("Failed to load asset trend:", error);
      setChartData(null);
    } finally {
      setLoading(false);
    }
  };

  // 初始化时使用传入的数据（7天数据，仅在图表刚展开时使用）
  useEffect(() => {
    if (propChartData && Array.isArray(propChartData) && selectedPeriod === "7") {
      setChartData(propChartData);
    }
  }, [propChartData]);

  // 当选择的天数或法币变化时，重新获取数据
  // 收起/展开时不重新调用，但切换天数时（包括切换到7天）都需要重新调用接口
  useEffect(() => {
    if (!isLogin || !currency || !expanded) return;
    
    // 切换天数时重新请求（包括切换到7天）
    fetchAssetTrend(selectedPeriod, currency);
  }, [isLogin, selectedPeriod, currency]);

  // 处理天数切换
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // 处理info icon点击，显示提示框
  const handleInfoClick = () => {
    Modal.info({
      title: t("home.note") || "Note",
      content: t("home.assetTrendNote") || "The daily assets in the chart are estimated based on the total net asset value at 0:00 (UTC) each day.",
      okText: t("home.confirm") || "Confirm",
      centered: true,
      wrapClassName: styles["asset-chart__modal"],
      width: 343,
      icon: null,
    });
  };

  // 处理图表数据
  const chartConfig = useMemo(() => {
    // 处理数据：提取数值，如果没有数据或数据为空，使用 [0] 显示 0 曲线
    let values = [];
    let labels = [];
    let dates = [];
    
    if (chartData && chartData.length > 0) {
      // 假设接口返回格式为 [{date: "2024-01-01", value: 1000}, ...]
      // 或者 [{time: timestamp, amount: 1000}, ...]
      values = chartData.map((item) => {
        if (typeof item === 'number') return item;
        return parseFloat(item.asset || item.value || item.amount || item) || 0;
      });
      labels = chartData.map((_, index) => index);
      
      // 提取日期用于显示
      dates = chartData.map((item) => {
        if (item.date) {
          return moment(item.date).format("MM-DD");
        } else if (item.time) {
          return moment(item.time).format("MM-DD");
        }
        return "";
      });
    } else {
      // 没有数据时，显示一条值为 0 的水平线
      values = [0, 0];
      labels = [0, 1];
      dates = ["", ""];
    }

    return { values, labels, dates };
  }, [chartData]);

  // 计算价格标签（只显示最大值）
  const priceLabels = useMemo(() => {
    if (!chartConfig || !chartConfig.values || chartConfig.values.length === 0) {
      return { max: null };
    }

    const values = chartConfig.values;
    const max = Math.max(...values);
    
    return { max };
  }, [chartConfig]);

  // 格式化价格显示
  const formatPrice = (value) => {
    if (value === null || value === undefined) return null;
    // 根据当前选择的法币格式化价格
    const currencySymbol = currency === "usd" ? "$" : currency === "cny" ? "¥" : "";
    return `${currencySymbol}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const chartConfigForChart = useMemo(() => {
    if (!chartConfig) return null;

    // 创建渐变（检查是否在浏览器环境中）
    // 根据设计稿：linear-gradient(180deg, rgba(34, 195, 107, 0.4) 0%, rgba(34, 195, 107, 0) 100%)
    let gradient = null;
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      gradient = ctx.createLinearGradient(0, 0, 0, 200);
      gradient.addColorStop(0, "rgba(34, 195, 107, 0.4)");
      gradient.addColorStop(1, "rgba(34, 195, 107, 0)");
    }

    return {
      labels: chartConfig.labels,
      datasets: [
        {
          label: "Asset Trend",
          data: chartConfig.values,
          borderColor: "#45BF59",
          backgroundColor: gradient || "rgba(34, 195, 107, 0.2)",
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 1.5,
        },
      ],
    };
  }, [chartConfig]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false,
        },
      },
      y: {
        display: false,
        grid: {
          display: false,
        },
      },
    },
  };

  if (!isLogin) {
    return null;
  }

  return (
    <div className={styles["asset-chart"]}>
      <div className={styles["asset-chart__container"]}>
        <div className={styles["asset-chart__header"]}>
          <span className={styles["asset-chart__title"]}>
            {t("home.assetTrend") || "Asset Trend"}
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={styles["asset-chart__info-icon"]}
            onClick={handleInfoClick}
          >
            <circle cx="8" cy="8" r="7" stroke="rgba(255, 255, 255, 0.4)" strokeWidth="1.5" fill="none" />
            <text x="8" y="11" textAnchor="middle" fontSize="10" fill="rgba(255, 255, 255, 0.4)" fontWeight="bold">i</text>
          </svg>
        </div>
        <div className={styles["asset-chart__content"]}>
          {chartConfigForChart && (
            <>
              <div className={styles["asset-chart__canvas"]}>
                <Line data={chartConfigForChart} options={chartOptions} />
              </div>
              <div className={styles["asset-chart__labels"]}>
                {priceLabels.max !== null && (
                  <div className={styles["asset-chart__value-top"]}>
                    {formatPrice(priceLabels.max)}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className={styles["asset-chart__dates"]}>
          {chartConfig && chartConfig.dates && chartConfig.dates.length >= 2 ? (
            <>
              <span className={styles["asset-chart__date-item"]}>
                {chartConfig.dates[0] || ""}
              </span>
              <span className={styles["asset-chart__date-item"]}>
                {chartConfig.dates[chartConfig.dates.length - 1] || ""}
              </span>
            </>
          ) : (
            <>
              <span className={styles["asset-chart__date-item"]}></span>
              <span className={styles["asset-chart__date-item"]}></span>
            </>
          )}
        </div>
        <div className={styles["asset-chart__period-selector"]}>
          <button
            className={`${styles["asset-chart__period-btn"]} ${
              selectedPeriod === "7" ? styles["asset-chart__period-btn--active"] : ""
            }`}
            onClick={() => handlePeriodChange("7")}
            disabled={loading}
          >
            {t("home.days7") || "7 days"}
          </button>
          <button
            className={`${styles["asset-chart__period-btn"]} ${
              selectedPeriod === "30" ? styles["asset-chart__period-btn--active"] : ""
            }`}
            onClick={() => handlePeriodChange("30")}
            disabled={loading}
          >
            {t("home.days30") || "30 days"}
          </button>
          <button
            className={`${styles["asset-chart__period-btn"]} ${
              selectedPeriod === "90" ? styles["asset-chart__period-btn--active"] : ""
            }`}
            onClick={() => handlePeriodChange("90")}
            disabled={loading}
          >
            {t("home.days90") || "90 days"}
          </button>
        </div>
      </div>
      <div className={styles["asset-chart__toggle"]} onClick={onToggleExpand}>
        <ExpandIcon expanded={expanded} onClick={onToggleExpand} />
      </div>
    </div>
  );
};

export default observer(AssetChart);
